import React, { useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Wrench, CheckCircle2, XCircle, PenLine, Send, BellRing, Printer, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { printRepairRequest } from "@/lib/printRepair";

const CUR_SYM = { USD: "US$", CAD: "CA$", GBP: "£", EUR: "€" };
export const repairMoney = (v, cur) => (v === null || v === undefined || v === "" ? "—" : `${CUR_SYM[cur] || "$"}${Number(v).toLocaleString(undefined, { minimumFractionDigits: 2 })}`);

export function DueCountdown({ dueDate, status }) {
  if (!dueDate || status !== "pending") return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const due = new Date(`${dueDate}T00:00:00`);
  const days = Math.round((due - today) / 86400000);
  const label = days > 1 ? `${days} days left` : days === 1 ? "1 day left" : days === 0 ? "due today" : `${-days}d overdue`;
  const cls = days < 0 ? "bg-rose-100 text-rose-800" : days <= 2 ? "bg-amber-100 text-amber-800" : "bg-sky-100 text-sky-800";
  return (
    <span className={`px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest ${cls}`} data-testid="repair-due-countdown">
      {label}
    </span>
  );
}

export function RevisitCountdown({ req }) {
  if (!req || req.status !== "revisit" || !req.revisit_by) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const by = new Date(`${req.revisit_by}T00:00:00`);
  const days = Math.round((by - today) / 86400000);
  const label = days > 1 ? `${days} days to revisit` : days === 1 ? "1 day to revisit" : days === 0 ? "revisit today" : `revisit ${-days}d overdue`;
  const cls = days < 0 ? "bg-rose-100 text-rose-800" : "bg-violet-100 text-violet-800";
  return (
    <span className={`px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest ${cls}`} data-testid="repair-revisit-countdown">
      {label}
    </span>
  );
}

const DETAIL_FIELDS = [
  ["Date", (r) => r.date],
  ["Decision due", (r) => r.due_date || "—"],
  ["PO / RO #", (r) => r.po_ro || "—"],
  ["Part number", (r) => r.part_number || "—"],
  ["Serial number", (r) => r.serial_number || "—"],
  ["Fleet type", (r) => r.fleet_type || "—"],
  ["Critical spares", (r) => r.critical_spares || "—"],
  ["Type of service", (r) => r.type_of_service || "—"],
  ["Estimated cost", (r) => repairMoney(r.estimated_cost, r.currency)],
  ["Evaluation fee (if denied)", (r) => repairMoney(r.evaluation_fee, r.currency)],
  ["Turnaround time", (r) => r.turnaround_time || "—"],
  ["Avg repair cost (RAAS)", (r) => repairMoney(r.avg_repair_cost, r.currency)],
  ["Current spares in stock", (r) => r.spares_in_stock || "—"],
  ["Consumption rate", (r) => r.consumption_rate || "—"],
  ["Min / Max details", (r) => r.min_max_details || "—"],
  ["RFQ 1", (r) => r.rfq1 || "—"],
  ["RFQ 2", (r) => r.rfq2 || "—"],
  ["RFQ 3", (r) => r.rfq3 || "—"],
];

const STATUS_BADGE = {
  pending: "bg-amber-100 text-amber-800",
  approved: "bg-emerald-100 text-emerald-800",
  denied: "bg-rose-100 text-rose-800",
  revisit: "bg-violet-100 text-violet-800",
};

export const REVISIT_OPTIONS = ["2 weeks", "1 month", "3 months", "6 months"];

export function RepairStatusBadge({ status }) {
  return (
    <span className={`px-2 py-0.5 rounded-full font-mono text-[10px] uppercase tracking-widest ${STATUS_BADGE[status] || "bg-gray-100 text-gray-700"}`}>
      {status}
    </span>
  );
}

export function RequestDetails({ req }) {
  return (
    <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm" data-testid="repair-detail-grid">
      {DETAIL_FIELDS.map(([label, fn]) => (
        <React.Fragment key={label}>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500 self-center">{label}</div>
          <div className="text-summit-ink">{fn(req)}</div>
        </React.Fragment>
      ))}
      {req.comments && (
        <>
          <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Comments</div>
          <div className="text-summit-ink whitespace-pre-wrap">{req.comments}</div>
        </>
      )}
    </div>
  );
}

/** Alert-style review modal shared by the Repairs page and the Tasks window.
 * Approving auto-signs with the viewer's full name + timestamp. */
export function RepairReviewDialog({ req, canDecide, canNudge, onOpenChange, onDecided, onResend }) {
  const { user } = useAuth();
  const [comment, setComment] = useState("");
  const [busy, setBusy] = useState(false);
  const [revisitTf, setRevisitTf] = useState("");
  const fullName = `${user?.first_name || ""} ${user?.last_name || ""}`.trim();

  const decide = async (decision) => {
    if (decision === "revisit" && !revisitTf) { toast.error("Pick a re-visit timeframe first."); return; }
    setBusy(true);
    try {
      await api.post(`/repair-approvals/${req.id}/decision`, {
        decision,
        comment: comment || null,
        revisit_timeframe: decision === "revisit" ? revisitTf : null,
      });
      toast.success(decision === "revisit" ? `Flagged for re-visit in ${revisitTf}` : `Request ${decision} — signed as ${fullName}`);
      onOpenChange(false);
      onDecided();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save decision");
    } finally {
      setBusy(false);
    }
  };

  const nudgeApprover = async () => {
    setBusy(true);
    try {
      await api.post(`/repair-approvals/${req.id}/nudge`);
      toast.success(`Nudge sent to ${req.approver_name}`);
      onDecided();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to send nudge");
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    setBusy(true);
    try {
      await api.post(`/repair-approvals/${req.id}/resend`);
      toast.success(`Request resent to ${req.approver_name}`);
      onOpenChange(false);
      onDecided();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to resend");
    } finally {
      setBusy(false);
    }
  };

  if (!req) return null;
  return (
    <Dialog open onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="repair-review-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 flex-wrap">
            <Wrench className="w-4 h-4" /> {req.description}
            <RepairStatusBadge status={req.status} />
            <DueCountdown dueDate={req.due_date} status={req.status} />
            <RevisitCountdown req={req} />
          </DialogTitle>
          <DialogDescription>
            Requested by {req.created_by_name} · sent to {req.approver_name}
          </DialogDescription>
        </DialogHeader>
        <RequestDetails req={req} />
        {req.status === "pending" && canNudge && !canDecide && (
          <div className="flex items-center justify-between gap-3 border border-amber-200 bg-amber-50/60 rounded-md px-3 py-2">
            <span className="text-xs text-amber-800">Waiting on {req.approver_name}{req.nudged_at ? ` · last nudged ${String(req.nudged_at).slice(0, 16).replace("T", " ")} UTC` : ""}</span>
            <Button size="sm" variant="outline" className="border-amber-300 text-amber-800 hover:bg-amber-100" onClick={nudgeApprover} disabled={busy} data-testid="repair-nudge-btn">
              <BellRing className="w-3.5 h-3.5 mr-1.5" /> Nudge
            </Button>
          </div>
        )}
        {req.status !== "pending" && (
          <div className="border border-summit-hairline rounded-md bg-summit-glacier-soft p-4 text-sm" data-testid="repair-decision-summary">
            <div className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Decision</div>
            <div className="mt-1">
              <span className="font-medium capitalize">{req.status === "revisit" ? "Re-visit" : req.status}</span> by {req.decided_by_name || req.approver_name} on {String(req.decided_at || "").slice(0, 10)}
            </div>
            {req.status === "revisit" && req.revisit_timeframe && (
              <div className="mt-1 inline-flex items-center gap-1.5 px-2 py-1 rounded bg-violet-100 text-violet-800 text-xs" data-testid="repair-revisit-info">
                <RotateCcw className="w-3.5 h-3.5" /> Re-visit in {req.revisit_timeframe} — by {req.revisit_by}
              </div>
            )}
            {req.status === "revisit" && req.revisit_by && (
              <div className="mt-1.5"><RevisitCountdown req={req} /></div>
            )}
            {req.decision_comment && <p className="mt-1 text-gray-700 whitespace-pre-wrap">{req.decision_comment}</p>}
            {req.signature_name && (
              <div className="mt-2 inline-flex items-center gap-1.5 font-display italic text-lg border-b border-gray-400 pr-6">
                <PenLine className="w-3.5 h-3.5 text-gray-400" /> {req.signature_name}
                <span className="not-italic font-mono text-[10px] text-gray-400 self-end pb-1">{String(req.decided_at || "").slice(0, 16).replace("T", " ")} UTC</span>
              </div>
            )}
            <div className="mt-3 flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={() => printRepairRequest(req)} data-testid="repair-print-btn">
                <Printer className="w-3.5 h-3.5 mr-1.5" /> Print / Save as PDF
              </Button>
              {(req.status === "denied" || req.status === "revisit") && onResend && (
                <Button size="sm" variant="outline" onClick={resend} disabled={busy} data-testid="repair-resend-btn">
                  <Send className="w-3.5 h-3.5 mr-1.5" /> Resend to {req.approver_name}
                </Button>
              )}
            </div>
          </div>
        )}
        {canDecide && req.status === "pending" && (
          <div className="border-t border-summit-hairline pt-4 space-y-3">
            <div>
              <Label className="text-xs uppercase tracking-widest text-gray-500">Comment (optional)</Label>
              <Textarea className="mt-1" rows={2} value={comment} onChange={(e) => setComment(e.target.value)} data-testid="repair-decision-comment" />
            </div>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="font-mono text-[10px] uppercase tracking-widest text-gray-400" data-testid="repair-autosign-note">
                <PenLine className="w-3 h-3 inline mr-1" />Signs as {fullName} + timestamp
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <div className="flex items-center gap-1.5">
                  <Select value={revisitTf} onValueChange={setRevisitTf}>
                    <SelectTrigger className="h-9 w-[130px] border-violet-200 text-violet-800" data-testid="repair-revisit-select">
                      <SelectValue placeholder="Re-visit in…" />
                    </SelectTrigger>
                    <SelectContent>
                      {REVISIT_OPTIONS.map((t) => (
                        <SelectItem key={t} value={t} data-testid={`repair-revisit-option-${t.replace(/\s+/g, "-")}`}>{t}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button variant="outline" className="border-violet-300 text-violet-700 hover:bg-violet-50" onClick={() => decide("revisit")} disabled={busy || !revisitTf} data-testid="repair-revisit-btn">
                    <RotateCcw className="w-4 h-4 mr-1.5" /> Re-visit
                  </Button>
                </div>
                <Button variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={() => decide("denied")} disabled={busy} data-testid="repair-deny-btn">
                  <XCircle className="w-4 h-4 mr-1.5" /> Deny
                </Button>
                <Button className="bg-emerald-700 hover:bg-emerald-800 text-white" onClick={() => decide("approved")} disabled={busy} data-testid="repair-approve-btn">
                  <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve
                </Button>
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
